import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import useAppAlertMessageAction from '../stores/appAlertMessage/appAlertMessageAction'
import useSnackbarAction from '../stores/snackbar/snackbarAction'
import useTranslate from '../i18n/useTranslate'
import { RoutePath } from '../router/routesConfig'
import { useHistory } from 'react-router-dom'
import useLoading from '../control/loading/useLoading'

export enum ErrorType {
    UNAUTHORIZED = 401,
    SERVER_ERROR = 500, // database error
    INPUT_INVALID = 400, // paramter ไม่ครบ หรือ ผิด
    NOT_FOUND = 404, // ไม่พบ
    TOKEN_EXPIRE = 999, // token หมดอายุ
}

type AuthorizationType = 'authorization' | 'unauthorized' | undefined
type ContentType = 'proxy' | string

const ERROR_EXPIRE_TOKEN = { response: { status: ErrorType.TOKEN_EXPIRE } }

const axiosBasic = axios.create()
const getHeaders = (contentType?: string, token?: string | null) => {
    if (token) {
        return {
            'Content-Type': 'application/json; charset=utf-8',
            Authorization: `Bearer ${token}`,
        }
    } else {
        return {
            'Content-Type': 'application/json; charset=utf-8',
        }
    }
}

const getAxios = (authorizationType: AuthorizationType) => {
    switch (authorizationType) {
        case 'unauthorized':
            return axiosBasic
        default:
            return axiosBasic
    }
}

axiosBasic.interceptors.request.use(
    (config) => {
        // Do something before request is sent
        return config
    },
    (error) => {
        // Do something with request error
        return Promise.reject(error)
    },
)

// Add a response interceptor
axiosBasic.interceptors.response.use(
    (response) => {
        return response
    },
    async (error) => {
        return Promise.reject(error)
    },
)

const useCallApi = () => {
    const appAlertMessageAction = useAppAlertMessageAction()
    const snackbarAction = useSnackbarAction()
    const translate = useTranslate()
    const history = useHistory()
    const { clearLoading } = useLoading()

    // const authenService = useAuthenService()
    // const autAction = authenAction()

    const axiosGet = async <Response = any>({
        url,
        config,
        contentType,
        authorizationType = 'unauthorized',
        token = null,
    }: {
        url: any
        config?: AxiosRequestConfig
        contentType?: ContentType
        authorizationType?: AuthorizationType
        token?: string | null
    }) => {
        let accessToken = null
        if (token) {
            accessToken = token
        } else {
            accessToken = localStorage.getItem('accessToken')
        }

        const expireDate = localStorage.getItem('tokenExpireDate')
        const currentDate = new Date()
        if (expireDate && currentDate.getTime() > Number(expireDate)) {
            console.log('Token Has Expire')
            clearLoading()
            localStorage.clear()
            history.replace(RoutePath.login, null)
            return catchFunc(ERROR_EXPIRE_TOKEN)
        } else {
            try {
                const request = await getAxios(authorizationType).get<Response>(url, {
                    headers: getHeaders(contentType, accessToken),
                    timeout: 180000,
                    ...config,
                })
                return returnResponse<Response>(request)
            } catch (error) {
                console.log('axiosGet => catch', error)

                // return Promise.reject(error)
                return catchFunc(error)
            }
        }
    }

    const axiosPost = async <Response = any, Parameter = any>({
        url,
        config,
        params,
        contentType,
        authorizationType = 'unauthorized',
        silent = false,
    }: {
        url: any
        params: Parameter
        config?: AxiosRequestConfig
        contentType?: ContentType
        authorizationType?: AuthorizationType
        //ไม่เด้ง dialog เวลา error ใช้กับ autosave
        silent?: boolean
    }) => {
        // let cancel
        // const request = getAxios(authorizationType).post<Response>(url, params, {
        //     headers: getHeaders(contentType),
        //     ...config,
        // })
        const accessToken = localStorage.getItem('accessToken')

        const expireDate = localStorage.getItem('tokenExpireDate')
        const currentDate = new Date()
        if (expireDate && currentDate.getTime() > Number(expireDate)) {
            console.log('Token Has Expire')
            clearLoading()
            localStorage.clear()
            history.replace(RoutePath.login, null)
            return catchFunc(ERROR_EXPIRE_TOKEN)
        } else {
            try {
                const request = await getAxios(authorizationType).post<Response>(url, params, {
                    headers: getHeaders(contentType, accessToken),
                    timeout: 180000,
                    ...config,
                })
                return returnResponse<Response>(request)
            } catch (error) {
                // return Promise.reject(error)
                return catchFunc(error, silent)
            }
        }
    }

    const axiosPut = async <Response = any, Parameter = any>({
        url,
        config,
        params,
        contentType,
        silent = false,
    }: {
        url: string
        params: Parameter
        config?: AxiosRequestConfig
        contentType?: ContentType
        authorizationType?: AuthorizationType
        //ไม่เด้ง dialog เวลา error ใช้กับ autosave
        silent?: boolean
    }) => {
        let cancel
        // const request = await axiosBasic.put<Response>(url, params, {
        //     headers: getHeaders(contentType),
        //     ...config,
        // })

        // return { request, cancel }
        const accessToken = localStorage.getItem('accessToken')
        const expireDate = localStorage.getItem('tokenExpireDate')
        const currentDate = new Date()
        if (expireDate && currentDate.getTime() > Number(expireDate)) {
            console.log('Token Has Expire')
            clearLoading()
            localStorage.clear()
            history.replace(RoutePath.login, null)
            return catchFunc(ERROR_EXPIRE_TOKEN)
        } else {
            try {
                const request = await axiosBasic.put<Response>(url, params, {
                    headers: getHeaders(contentType, accessToken),
                    timeout: 180000,
                    ...config,
                })
                return returnResponse<Response>(request)
            } catch (error) {
                // return Promise.reject(error)
                return catchFunc(error, silent)
            }
        }
    }

    const axiosDelete = async <Response = any, Parameter = any>({
        url,
        config,
        contentType,
        authorizationType = 'unauthorized',
    }: // params,
    {
        url: string
        config?: AxiosRequestConfig
        contentType?: ContentType
        authorizationType?: AuthorizationType
        // params: Parameter
    }) => {
        // let cancel
        // const request = getAxios(authorizationType).delete<Response>(url, {
        //     headers: getHeaders(contentType),
        //     data: params ? params : null,
        //     ...config,
        // })

        // return { request, cancel }
        const accessToken = localStorage.getItem('accessToken')

        const expireDate = localStorage.getItem('tokenExpireDate')
        const currentDate = new Date()
        if (expireDate && currentDate.getTime() > Number(expireDate)) {
            console.log('Token Has Expire')
            clearLoading()
            localStorage.clear()
            history.replace(RoutePath.login, null)
            return catchFunc(ERROR_EXPIRE_TOKEN)
        } else {
            try {
                const request = await getAxios(authorizationType).delete<Response>(url, {
                    headers: getHeaders(contentType, accessToken),
                    timeout: 180000,
                    // data: params ? params : null,
                    ...config,
                })
                return returnResponse<Response>(request)
            } catch (error) {
                // return Promise.reject(error)
                return catchFunc(error)
            }
        }
    }

    const returnResponse = <T>(response: AxiosResponse<T>) => {
        if (response.status === 401) {
            throw '401'
        } else {
            return response
        }
    }
    /**
     * silent = true  ใช้กับ request ที่ผู้ใช้ไม่ได้สั่งเอง เช่น autosave
     *                จะไม่เด้ง dialog รบกวนตอนกำลังพิมพ์ แต่ยัง log และ reject ตามเดิม
     */
    const catchFunc = (error: any, silent = false) => {
        // request ที่เรายกเลิกเอง (เช่น พิมพ์คำค้นใหม่ทับ) ไม่ใช่ error จริง
        // ห้ามเด้ง dialog ให้ผู้ใช้เห็น ปล่อย reject ให้ผู้เรียกจัดการเอง
        if (axios.isCancel(error)) {
            return Promise.reject(error)
        }
        console.log('catchFunc :', error?.response?.status)
        const errorStatusCode = error?.response?.status

        const httpMethod = String(error?.config?.method ?? '').toLowerCase()
        const isReadRequest = httpMethod === 'get'
        const failMessageKey = isReadRequest ? 'ALERT.CONTENT.LOAD_NOT_SUCCESS' : 'ALERT.CONTENT.SAVE_NOT_SUCCESS'
        if (errorStatusCode === ErrorType.UNAUTHORIZED) {
            // snackbarAction.openSnackbar({ message: 'Unauthorized', type: 'error' })
            clearLoading()
            localStorage.clear()
            history.push(RoutePath.login)
            //goto login
        } else if (errorStatusCode === ErrorType.TOKEN_EXPIRE) {
            // snackbarAction.openSnackbar({ message: 'TOKEN HAS EXPIRE', type: 'error' })
            clearLoading()
            localStorage.clear()
            history.push(RoutePath.login)
            //goto login
        } else if (error?.response) {
            clearLoading()
            console.error('API error response:', error)
            if (!silent) {
                appAlertMessageAction.openAlertMessage({
                    title: translate.get('ALERT.TITLE.ERROR'),
                    message: translate.get(failMessageKey),
                })
            }
        } else {
            clearLoading()
            console.error('API error without response:', error)
            if (!silent) {
                appAlertMessageAction.openAlertMessage({
                    title: translate.get('ALERT.TITLE.ERROR'),
                    message: translate.get('ALERT.CONTENT.CONTACT_ADMIN'),
                })
            }
        }
        return Promise.reject(error)
    }

    return { axiosGet, axiosPost, axiosPut, axiosDelete }
}
export default useCallApi
